/**
 * Accordion Utilities
 * Shared accordion functionality for FAQ sections and product pages
 */

const AccordionManager = {
  /**
   * Initialize accordion functionality
   * @param {Object} options - Configuration options
   * @param {string} options.containerSelector - Selector for accordion container
   * @param {string} options.toggleSelector - Selector for toggle buttons (default: '[data-accordion-toggle]')
   * @param {string} options.itemSelector - Selector for accordion items (default: '.accordion-item')
   * @param {string} options.activeClass - CSS class for active state (default: 'is-open')
   * @param {boolean} options.singleOpen - Only allow one item open at a time (default: true)
   * @param {boolean} options.ariaSupport - Add aria-expanded attribute (default: true)
   * @param {Function} options.onToggle - Callback function (item, isOpen, context)
   */
  init: function(options = {}) {
    const {
      containerSelector,
      toggleSelector = '[data-accordion-toggle]',
      itemSelector = '.accordion-item',
      activeClass = 'is-open',
      singleOpen = true,
      ariaSupport = true,
      onToggle = null
    } = options;

    if (!containerSelector) {
      console.error('AccordionManager: containerSelector is required');
      return;
    }

    const containers = document.querySelectorAll(containerSelector);

    if (containers.length === 0) {
      return; // No containers found, silently return
    }

    containers.forEach(container => {
      container.addEventListener('click', (e) => {
        const toggle = e.target.closest(toggleSelector);
        if (!toggle) return;

        e.preventDefault();
        e.stopPropagation();

        const item = toggle.closest(itemSelector);
        if (!item) return;

        const isOpen = item.classList.contains(activeClass);

        // Close other items if single open mode
        if (singleOpen && !isOpen) {
          container.querySelectorAll(`${itemSelector}.${activeClass}`).forEach(openItem => {
            if (openItem !== item) {
              openItem.classList.remove(activeClass);
              if (ariaSupport) {
                const openToggle = openItem.querySelector(toggleSelector);
                if (openToggle) openToggle.setAttribute('aria-expanded', 'false');
              }
            }
          });
        }

        // Toggle current item
        item.classList.toggle(activeClass);

        if (ariaSupport) {
          toggle.setAttribute('aria-expanded', !isOpen);
        }

        // Call callback if provided
        if (onToggle) {
          onToggle(item, !isOpen, {
            toggle: toggle,
            container: container
          });
        }
      });
    });
  }
};

// Export for use in other scripts
if (typeof window !== 'undefined') {
  window.AccordionManager = AccordionManager;
}
